/*
 * Atelier Sedelle: three-question guide, transparent recommendations and lead capture.
 * Vanilla JS, no dependencies. Piece data comes from the section's JSON (customer-safe only).
 *
 * Privacy: the URL carries only an opaque code of the three answers (?s=) and the origin product
 * (?from=); never email, name or free text. Analytics events carry the same non-personal answers.
 * Leads are posted to Shopify's native contact form; the newsletter (customer) form is used only
 * when the consent box is ticked.
 */

const NEED = {
  part: 'Dar densidad a la raya',
  crown: 'Cubrir principalmente la coronilla',
  wide: 'Una cobertura más amplia',
  hairline: 'Recuperar la línea frontal',
  volume_length: 'Más volumen o longitud',
  complete: 'Una solución completa',
  unsure: 'No estoy segura',
};
const NEED_PHRASE = {
  part: 'dar densidad a la raya',
  crown: 'cubrir sobre todo la coronilla',
  wide: 'una cobertura más amplia',
  hairline: 'recuperar la línea frontal',
  volume_length: 'más volumen o longitud',
  complete: 'una solución completa',
};
const NEED_NEAR = { part: ['crown'], crown: ['part', 'wide'], wide: ['crown', 'complete'], complete: ['wide'], volume_length: [], hairline: [] };
const LENGTH = { '10-12': '10–12″', 14: '14″', 16: '16″', 18: '18″ o más', unsure: 'Largo por definir' };
const LENGTH_VALUES = { '10-12': [10, 11, 12], 14: [14], 16: [16], 18: [18, 20, 22, 24] };
const TONE = {
  black: 'Negro / muy oscuro',
  dark_brown: 'Castaño oscuro',
  medium_brown: 'Castaño medio',
  light_brown: 'Castaño claro',
  blonde: 'Rubio',
  grey: 'Canas / gris',
  red: 'Pelirrojo / cobrizo',
  unsure: 'Tono por definir',
};
const COVERAGE = { small: 'Raya', medium: 'Coronilla', wide: 'Amplia', full: 'Muy amplia', hairline: 'Línea frontal' };
const TYPE = { topper: 'Topper', hair_system: 'Pieza integral', wig: 'Peluca', clip_in: 'Extensiones' };
const GROUPS = [
  ['raya_coronilla', 'Raya y coronilla'],
  ['cobertura_amplia', 'Cobertura amplia'],
  ['maxima_naturalidad', 'Máxima naturalidad'],
  ['integracion', 'Integración'],
  ['volumen_longitud', 'Volumen'],
  ['premium', 'Opciones premium'],
  ['solucion_completa', 'Soluciones completas'],
];
const EXPLORE_VISIBLE = 6;
// Why a piece can fit, in prudent words: an orientation, never a diagnosis or a promise.
const FIT = {
  small: 'Pensada para la raya: una base pequeña y discreta, que no cubre de más.',
  medium: 'Pensada para la coronilla, sin llegar a ser una pieza grande.',
  wide: 'Pensada para una zona amplia: coronilla y raya con margen, sin llegar a ser una peluca.',
  full: 'Pensada para cubrir una zona grande de la parte superior.',
  hairline: 'Pensada para la línea frontal, de oreja a oreja, donde se notan las entradas.',
  wig: 'Una solución completa: no depende de tu propio cabello para integrarse.',
  clip_in: 'Pensada para sumar largo y cuerpo en medios y puntas, sin base sobre la coronilla.',
};
const REASONS = ['Demasiado caro', 'Demasiada cobertura', 'Poca cobertura', 'Largo', 'Tono', 'Plazo', 'Prefiero otra construcción', 'Otro'];
const KIND = {
  save: { label: 'Guardar selección', level: 'INTERESADO', tag: 'atelier-interested' },
  request: { label: 'Solicitud de pieza', level: 'CALIENTE', tag: 'atelier-hot' },
  search: { label: 'Nueva búsqueda', level: 'CALIENTE', tag: 'atelier-hot' },
  precise: { label: 'Búsqueda precisa (sin coincidencias)', level: 'CALIENTE', tag: 'atelier-hot' },
};
/*
 * Shareable selection code (?s=…): one character per answer, from fixed tables, so a URL never
 * spells out a need, a length or a tone. Stable on purpose: never change or reuse a character,
 * only add new ones. '0' means "not answered yet". The Home shortcuts in
 * sections/sedelle-atelier-cta.liquid use the same need characters.
 */
const CODE = {
  need: { part: 'q', crown: '7', wide: 'm', hairline: 'x', volume_length: '3', complete: 'h', unsure: 'b' },
  length: { '10-12': 'e', 14: '9', 16: 'p', 18: 'z', unsure: '5' },
  tone: { black: 'k', dark_brown: 'a', medium_brown: 'w', light_brown: '2', blonde: 'n', grey: 'j', red: '8', unsure: 't' },
};
const CODE_ORDER = ['need', 'length', 'tone'];
const encodeState = (answers) => CODE_ORDER.map((key) => (answers[key] && CODE[key][answers[key]]) || '0').join('');
const decodeState = (code) => {
  const state = {};
  if (typeof code !== 'string' || code.length !== CODE_ORDER.length) return state;
  CODE_ORDER.forEach((key, i) => {
    const hit = Object.keys(CODE[key]).find((value) => CODE[key][value] === code[i]);
    if (hit) state[key] = hit;
  });
  return state;
};
const PENDING_KEY = 'sedelle-atelier-pending';
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function track(name, data = {}) {
  const payload = { ...data };
  try {
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (e) {
    /* analytics must never break the guide */
  }
  (window.dataLayer = window.dataLayer || []).push({ event: name, ...payload });
  document.dispatchEvent(new CustomEvent('sedelle:atelier', { detail: { name, ...payload } }));
}

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const inches = (list) => list.map((l) => `${l}″`).join(', ').replace(/, ([^,]*)$/, ' o $1');

class SedelleAtelier extends HTMLElement {
  connectedCallback() {
    const data = JSON.parse(this.querySelector('[data-atelier-data]').textContent);
    this.pieces = data.pieces.map((p) => ({ ...p, lengths: p.lengths.map(Number).filter(Boolean) }));
    this.answers = { need: null, length: null, tone: null };
    this.from = null;
    this.step = 1;
    this.shown = [];
    this.rejected = {};
    this.contact = { email: '', name: '' };
    this.requested = null;

    this.form = this.querySelector('[data-steps]');
    this.cards = this.querySelector('[data-cards]');
    this.results = this.querySelector('#atelier-resultado');
    this.nextButton = this.querySelector('[data-next]');
    this.backButton = this.querySelector('[data-back]');

    this.readUrl();
    this.bind();
    this.render({ focus: false, animate: false });
    // A saved selection link (from the email, for instance) opens on the selection itself.
    if (this.step >= 4) requestAnimationFrame(() => this.results.scrollIntoView({ block: 'start' }));
    this.restorePending();
    track('atelier_open', { from: this.from || undefined });
  }

  /* ---------- URL state (opaque code, non-personal only) ---------- */
  readUrl() {
    const params = new URLSearchParams(location.search);
    let url = decodeState(params.get('s'));
    // Links shared before the code spelled the answers out (?need=…&length=…&tone=…):
    // they still open the same selection, and the URL is cleaned straight away.
    const legacy = CODE_ORDER.some((key) => params.has(key));
    if (legacy && !params.has('s')) {
      const pick = (key, allowed) => (allowed.includes(params.get(key)) ? params.get(key) : null);
      url = { need: pick('need', Object.keys(NEED)), length: pick('length', Object.keys(LENGTH)), tone: pick('tone', Object.keys(TONE)) };
    }
    // The URL decides the step; answers given further on stay selected when she steps back.
    for (const key of CODE_ORDER) if (url[key]) this.answers[key] = url[key];
    this.from = params.get('from') ? params.get('from').replace(/[^a-z0-9-]/gi, '').slice(0, 60) : this.from;
    if (!url.need) this.step = 1;
    else if (!url.length) this.step = 2;
    else if (!url.tone) this.step = 3;
    else this.step = 4;
    if (legacy) history.replaceState(history.state, '', this.urlFor(this.step));
  }

  urlFor(step, { share = false } = {}) {
    // Other parameters (campaign tags, for instance) stay as they are; the answers only ever
    // travel as the code, and Shopify's one-off form flags are dropped.
    const params = share ? new URLSearchParams() : new URLSearchParams(location.search);
    ['s', ...CODE_ORDER, 'from', 'contact_posted', 'customer_posted'].forEach((key) => params.delete(key));
    const code = encodeState({
      need: step > 1 ? this.answers.need : null,
      length: step > 2 ? this.answers.length : null,
      tone: step > 3 ? this.answers.tone : null,
    });
    if (code !== '000') params.set('s', code);
    if (this.from && !share) params.set('from', this.from);
    const query = params.toString();
    return `${location.pathname}${query ? `?${query}` : ''}`;
  }

  selectionLink() {
    return `${location.origin}${this.urlFor(4, { share: true })}`;
  }

  /*
   * Attribution for orders that start here: a hidden cart attribute with the piece and the three
   * answers, nothing else. Fire-and-forget (keepalive): the product link never waits for it.
   */
  attribute(piece) {
    const { need, length, tone } = this.answers;
    const value = [piece.id, need || '-', length || '-', tone || '-'].join('|');
    try {
      fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _atelier: value } }),
        credentials: 'same-origin',
        keepalive: true,
      }).catch(() => {});
    } catch (e) {
      /* attribution must never block the product page */
    }
  }

  /* ---------- Events ---------- */
  bind() {
    this.querySelector('[data-start]')?.addEventListener('click', (event) => {
      event.preventDefault();
      track('atelier_started', { entry: 'intro' });
      this.started = true;
      this.focusGuide();
    });

    // Tap/click selects and moves on; keyboard selection waits for "Continuar".
    this.form.addEventListener('pointerup', (event) => {
      if (event.target.closest('.sa-option')) this.pointerPick = true;
    });
    this.form.addEventListener('change', (event) => {
      const input = event.target;
      if (!['need', 'length', 'tone'].includes(input.name)) return;
      this.answers[input.name] = input.value;
      if (!this.started) {
        this.started = true;
        track('atelier_started', { entry: 'guide' });
      }
      this.nextButton.disabled = false;
      if (this.pointerPick) {
        this.pointerPick = false;
        clearTimeout(this.advanceTimer);
        this.advanceTimer = setTimeout(() => this.next(), reduceMotion() ? 0 : 260);
      }
    });
    this.nextButton.addEventListener('click', () => this.next());
    this.backButton.addEventListener('click', () => this.back());
    window.addEventListener('popstate', () => {
      this.readUrl();
      this.render({ focus: true });
    });

    this.querySelector('[data-restart]').addEventListener('click', () => {
      this.answers = { need: null, length: null, tone: null };
      this.form.reset();
      this.go(1, { push: true });
    });

    this.querySelector('[data-open-search]').addEventListener('click', (event) => this.openPanel('#atelier-busqueda', event.currentTarget, 'atelier_search_again'));
    this.querySelector('[data-open-explore]').addEventListener('click', (event) => {
      this.renderExplore();
      this.openPanel('#atelier-explorar', event.currentTarget, 'atelier_explore_open');
    });

    this.cards.addEventListener('click', (event) => this.onCardClick(event));
    this.querySelector('[data-explore]').addEventListener('click', (event) => this.onCardClick(event));

    this.addEventListener('click', (event) => {
      const ig = event.target.closest('[data-instagram]');
      if (ig) track('atelier_instagram_click', { place: ig.closest('dialog') ? 'duda' : ig.closest('.sa-tone-help') ? 'tono' : 'bloque' });
      const close = event.target.closest('[data-close]');
      if (close) close.closest('dialog').close();
    });
    this.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });

    this.querySelectorAll('[data-lead-form]').forEach((form) => form.addEventListener('submit', (event) => this.onLeadSubmit(event)));
  }

  next() {
    const field = { 1: 'need', 2: 'length', 3: 'tone' }[this.step];
    if (!field || !this.answers[field]) return;
    track(`atelier_step_${this.step}`, { [field]: this.answers[field] });
    this.go(this.step + 1, { push: true });
  }

  back() {
    if (this.step <= 1) return;
    if (history.state?.atelier) history.back();
    else this.go(this.step - 1, { push: false });
  }

  go(step, { push }) {
    this.step = step;
    const url = this.urlFor(step);
    if (push) history.pushState({ atelier: step }, '', url);
    else history.replaceState({ atelier: step }, '', url);
    this.render({ focus: true });
  }

  focusGuide() {
    const guide = this.querySelector('#atelier-orientador');
    guide.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    guide.querySelector('.sa-step:not([hidden]) input')?.focus({ preventScroll: true });
  }

  /* ---------- Rendering ---------- */
  render({ focus, animate = true }) {
    const guide = this.querySelector('#atelier-orientador');
    const inResults = this.step >= 4;

    this.querySelectorAll('.sa-step').forEach((fieldset) => {
      const n = Number(fieldset.dataset.step);
      const visible = n === Math.min(this.step, 3);
      fieldset.hidden = !visible;
      const name = { 1: 'need', 2: 'length', 3: 'tone' }[n];
      fieldset.querySelectorAll('input').forEach((input) => {
        input.checked = this.answers[name] === input.value;
      });
      if (visible && animate && !reduceMotion()) {
        fieldset.classList.remove('is-entering');
        void fieldset.offsetWidth;
        fieldset.classList.add('is-entering');
      }
    });

    const current = Math.min(this.step, 3);
    this.querySelector('[data-progress-label]').textContent = inResults ? 'Tu selección está lista' : `Paso ${current} de 3`;
    this.querySelectorAll('[data-dot]').forEach((dot) => {
      const n = Number(dot.dataset.dot);
      dot.classList.toggle('is-done', n < current || inResults);
      dot.classList.toggle('is-current', n === current && !inResults);
    });
    this.backButton.hidden = this.step === 1;
    this.nextButton.disabled = !this.answers[{ 1: 'need', 2: 'length', 3: 'tone' }[current]];
    this.nextButton.textContent = current === 3 ? 'Ver mi selección' : 'Continuar';
    guide.classList.toggle('is-complete', inResults);

    const context = this.querySelector('[data-context]');
    const origin = this.from && this.pieces.find((p) => p.handle === this.from);
    context.hidden = !origin;
    if (origin) context.textContent = `Vienes de ${origin.name}. Te enseñamos otras bases, largos o tonos que pueden encajar.`;

    if (inResults) {
      this.showResults({ focus, animate });
    } else {
      this.results.hidden = true;
      this.querySelector('[data-follow]').hidden = true;
      if (focus) {
        const step = guide.querySelector('.sa-step:not([hidden])');
        (step.querySelector('input:checked') || step.querySelector('input'))?.focus({ preventScroll: true });
        guide.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    }
  }

  /* ---------- Recommendation rules ---------- */
  scorePiece(piece) {
    const { need, length, tone } = this.answers;
    let needScore;
    if (!need || need === 'unsure') needScore = piece.isProduct ? 24 : 16;
    else if (piece.needs.includes(need)) needScore = 40;
    else if (piece.needs.some((n) => (NEED_NEAR[need] || []).includes(n))) needScore = 14;
    else return null;

    let lengthScore = 12;
    let lengthMatch = null;
    if (length && length !== 'unsure') {
      const wanted = LENGTH_VALUES[length];
      if (piece.lengths.some((l) => wanted.includes(l))) {
        lengthScore = 25;
        lengthMatch = 'exact';
      } else {
        const lo = Math.min(...wanted) - 2;
        const hi = Math.max(...wanted) + 2;
        if (!piece.lengths.some((l) => l >= lo && l <= hi)) return null;
        lengthScore = 10;
        lengthMatch = 'near';
      }
    }

    let toneScore = 12;
    if (tone && tone !== 'unsure') {
      if (!piece.tones.includes(tone)) return null;
      toneScore = 25;
    }

    let score = needScore + lengthScore + toneScore + Number(piece.priority || 0);
    if (piece.isProduct && piece.available) score += 10;
    if (piece.stock === 'stock') score += 4;
    if (this.from && piece.handle === this.from) score -= 15;
    if (this.rejected[piece.id]) score -= 40;
    return { piece, score, exactNeed: needScore === 40, lengthMatch };
  }

  recommend() {
    const scored = this.pieces
      .map((p) => this.scorePiece(p))
      .filter((r) => r && r.score >= 55)
      .sort((a, b) => b.score - a.score || b.piece.priority - a.piece.priority);
    // One piece per design family: a bigger catalogue sharpens the match instead of repeating it.
    const families = new Set();
    const distinct = scored.filter((r) => {
      if (!r.piece.group) return true;
      if (families.has(r.piece.group)) return false;
      families.add(r.piece.group);
      return true;
    });
    const exact = distinct.filter((r) => r.exactNeed || !this.answers.need || this.answers.need === 'unsure');
    let pool = exact.length >= 2 ? exact : [...exact, ...distinct.filter((r) => !exact.includes(r))];
    let picks = pool.slice(0, 3);
    if (picks.length === 3 && picks[2].score < picks[0].score - 22) picks = picks.slice(0, 2);
    // Something she can buy today, when a store product is a reasonable fit.
    if (picks.length && !picks.some((r) => r.piece.isProduct)) {
      const product = distinct.find((r) => r.piece.isProduct && r.piece.available && r.score >= 70);
      if (product) picks = [...picks.slice(0, 2), product];
    }
    return picks;
  }

  /* "Por qué encaja contigo": coverage, length and tone tied to her answers, then the base itself. */
  reasons(result) {
    const { piece, exactNeed, lengthMatch } = result;
    const { need, length, tone } = this.answers;
    const list = [];
    if (!exactNeed && need && need !== 'unsure') {
      list.push(`Una opción a considerar si buscas ${NEED_PHRASE[need]}: no es exactamente lo que has descrito, pero es lo más cercano.`);
    }
    const fit = FIT[piece.baseType === 'wig' ? 'wig' : piece.baseType === 'clip_in' ? 'clip_in' : piece.coverage];
    if (fit) list.push(fit);
    if (lengthMatch === 'exact') list.push(`Existe en ${LENGTH[length]}, el largo que has indicado.`);
    else if (lengthMatch !== 'near' && piece.lengths.length) list.push(`Se hace en ${inches(piece.lengths)}.`);
    if (tone && tone !== 'unsure') list.push(`Hay tonos en tu familia (${TONE[tone].toLowerCase()}); si dudas con el exacto, te ayudamos.`);
    else list.push('Si dudas con el tono, te ayudamos con una foto de tu raíz.');
    if (piece.construction) list.push(piece.construction);
    return list;
  }

  limitText(result) {
    const notes = [];
    if (result.piece.notes) notes.push(result.piece.notes);
    if (result.lengthMatch === 'near') notes.push(`No existe en ${LENGTH[this.answers.length]}: el largo más cercano es ${inches(result.piece.lengths)}.`);
    if (result.piece.stock === 'made_to_order' && !/pedido/i.test(result.piece.notes || '')) notes.push('Se prepara bajo pedido.');
    return notes.join(' ');
  }

  showResults({ focus, animate }) {
    this.results.hidden = false;
    const picks = this.recommend();
    this.shown = picks.map((r) => r.piece);
    const { need, length, tone } = this.answers;
    this.querySelector('[data-answers]').textContent = [NEED[need], LENGTH[length], TONE[tone]].filter(Boolean).join(' · ');

    const empty = this.querySelector('[data-empty]');
    empty.hidden = picks.length > 0;
    this.querySelector('[data-results-title]').hidden = picks.length === 0;
    this.cards.innerHTML = picks.map((r, i) => this.cardHtml(r, i)).join('');
    this.cards.classList.toggle('sa-cards--two', picks.length === 2);
    this.cards.classList.toggle('sa-cards--one', picks.length === 1);
    this.cards.classList.toggle('is-revealing', animate && !reduceMotion());
    this.querySelector('[data-follow]').hidden = false;

    track('atelier_results_viewed', { need, length, tone, count: picks.length, pieces: picks.map((r) => r.piece.id).join(',') });
    const products = picks.filter((r) => r.piece.isProduct);
    if (products.length) track('atelier_product_view', { products: products.map((r) => r.piece.id).join(',') });

    if (focus) {
      this.results.focus({ preventScroll: true });
      this.results.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }

  media(piece, sizes) {
    if (!piece.img) {
      return `<div class="sa-card__slot" aria-hidden="true"><svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M12 2l2.35 6.76 7.16.15-5.71 4.33 2.08 6.85L12 16l-5.88 4.09 2.08-6.85-5.71-4.33 7.16-.15z" fill="none" stroke="currentColor" stroke-width="1" vector-effect="non-scaling-stroke"/></svg></div>`;
    }
    return `<img src="${esc(piece.img.src)}" srcset="${esc(piece.img.srcset)}" sizes="${sizes}" alt="${esc(piece.img.alt)}" loading="lazy" decoding="async" width="800" height="${Math.round(800 / (piece.img.ratio || 1))}">`;
  }

  actions(piece, compact) {
    const primary = piece.isProduct
      ? `<a class="button sa-button" href="${esc(piece.url)}" data-product-link="${esc(piece.id)}">${compact ? 'Ver producto' : piece.available ? 'Elegir esta pieza' : 'Ver producto'} <span aria-hidden="true">→</span></a>`
      : `<button type="button" class="button sa-button" data-request="${esc(piece.id)}">Solicitar esta pieza <span aria-hidden="true">→</span></button>`;
    return `${primary}<button type="button" class="sa-link" data-doubt="${esc(piece.id)}">Tengo una duda</button>`;
  }

  cardHtml(result, index) {
    const { piece } = result;
    const limit = this.limitText(result);
    const facts = [
      ['Base', piece.baseSize],
      ['Densidad', piece.density],
      ['Plazo', piece.lead],
    ].filter(([, v]) => v);
    const titleId = `SaCard-${esc(piece.id)}`;
    return `
      <li class="sa-card" style="--i:${index}" data-piece="${esc(piece.id)}">
        <div class="sa-card__media">${this.media(piece, '(min-width: 990px) 30vw, (min-width: 750px) 45vw, 100vw')}</div>
        <div class="sa-card__body">
          <p class="sa-card__kicker">Selección personal <span aria-hidden="true">★</span> ${esc(TYPE[piece.baseType] || '')}</p>
          <h3 class="sa-card__title" id="${titleId}">${esc(piece.name)}</h3>
          ${piece.tagline ? `<p class="sa-card__tagline">${esc(piece.tagline)}</p>` : ''}
          <div class="sa-card__why">
            <p class="sa-card__label" id="${titleId}-why">Por qué encaja contigo</p>
            <ul class="sa-card__reasons" role="list" aria-labelledby="${titleId}-why ${titleId}">${this.reasons(result).map((r) => `<li>${esc(r)}</li>`).join('')}</ul>
          </div>
          <dl class="sa-card__facts">${facts.map(([k, v]) => `<div><dt>${k}</dt><dd>${esc(v)}</dd></div>`).join('')}</dl>
          ${limit ? `<p class="sa-card__limit"><span>A tener en cuenta.</span> ${esc(limit)}</p>` : ''}
          <p class="sa-card__price"><span class="sa-card__price-value">${esc(piece.priceText)}</span>${piece.priceNote ? `<span class="sa-card__price-note">${esc(piece.priceNote)}</span>` : ''}</p>
          <div class="sa-card__actions">${this.actions(piece, false)}</div>
          <div class="sa-card__reject">
            <button type="button" class="sa-link sa-link--quiet" data-reject="${esc(piece.id)}" aria-expanded="false">No es para mí</button>
            <div class="sa-reasons" data-reasons hidden>
              <p class="sa-reasons__label">¿Por qué? <span class="sa-field__optional">(opcional)</span></p>
              <div class="sa-reasons__list">${REASONS.map((r) => `<button type="button" class="sa-chip sa-chip--button" data-reason="${esc(r)}">${esc(r)}</button>`).join('')}</div>
            </div>
          </div>
        </div>
      </li>`;
  }

  renderExplore() {
    const holder = this.querySelector('[data-explore]');
    if (holder.childElementCount) return;
    holder.innerHTML = GROUPS.map(([key, label]) => {
      const list = this.pieces.filter((p) => p.category === key && p.inExplore !== false).sort((a, b) => b.priority - a.priority);
      if (!list.length) return '';
      const rest = list.length - EXPLORE_VISIBLE;
      const more = rest > 0 ? `<button type="button" class="sa-link sa-group__more" data-explore-more aria-expanded="false">Ver ${rest === 1 ? 'otra opción' : `las ${rest} restantes`} <span aria-hidden="true">→</span></button>` : '';
      return `<section class="sa-group"><h3 class="sa-group__title">${esc(label)}</h3><ul class="sa-mini-list" role="list">${list
        .map(
          (p, i) => `<li class="sa-mini" data-piece="${esc(p.id)}"${i >= EXPLORE_VISIBLE ? ' hidden data-explore-extra' : ''}>
            <div class="sa-mini__media">${this.media(p, '160px')}</div>
            <div class="sa-mini__body">
              <p class="sa-mini__type">${esc(TYPE[p.baseType] || '')} · ${esc(COVERAGE[p.coverage] || '')}</p>
              <h4 class="sa-mini__title">${esc(p.name)}</h4>
              ${p.tagline ? `<p class="sa-mini__text">${esc(p.tagline)}</p>` : ''}
              <p class="sa-mini__meta">${esc([p.baseSize, p.lengths.length ? inches(p.lengths) : '', p.lead].filter(Boolean).join(' · '))}</p>
              <p class="sa-mini__price">${esc(p.priceText)}</p>
              <div class="sa-mini__actions">${this.actions(p, true)}</div>
            </div>
          </li>`
        )
        .join('')}</ul>${more}</section>`;
    }).join('');
  }

  openPanel(selector, trigger, eventName) {
    const panel = this.querySelector(selector);
    const opening = panel.hidden;
    panel.hidden = !opening;
    trigger.setAttribute('aria-expanded', String(opening));
    if (opening) {
      track(eventName, { need: this.answers.need, length: this.answers.length, tone: this.answers.tone });
      this.prefill(panel);
      panel.focus({ preventScroll: true });
      panel.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }

  prefill(scope) {
    scope.querySelectorAll('[data-email]').forEach((input) => {
      if (!input.value && this.contact.email) input.value = this.contact.email;
    });
    scope.querySelectorAll('[data-name]').forEach((input) => {
      if (!input.value && this.contact.name) input.value = this.contact.name;
    });
  }

  pieceById(id) {
    return this.pieces.find((p) => p.id === id);
  }

  onCardClick(event) {
    const product = event.target.closest('[data-product-link]');
    if (product) {
      const piece = this.pieceById(product.dataset.productLink);
      if (piece?.isProduct && piece.url) this.attribute(piece);
      track('atelier_public_product_click', { piece: product.dataset.productLink });
      return;
    }
    const request = event.target.closest('[data-request]');
    if (request) {
      const piece = this.pieceById(request.dataset.request);
      this.requested = piece;
      const dialog = this.querySelector('[data-request-dialog]');
      dialog.querySelector('[data-request-name]').textContent = piece.name;
      this.resetForm(dialog.querySelector('form'));
      this.prefill(dialog);
      dialog.showModal();
      track('atelier_piece_request_open', { piece: piece.id });
      return;
    }
    const doubt = event.target.closest('[data-doubt]');
    if (doubt) {
      const piece = this.pieceById(doubt.dataset.doubt);
      const dialog = this.querySelector('[data-help-dialog]');
      const mail = dialog.querySelector('[data-help-mail]');
      const subject = encodeURIComponent(`Atelier · ${piece.name}`);
      const body = encodeURIComponent(`Hola, tengo una duda sobre ${piece.name}.\n\nMi selección: ${this.selectionLink()}\n\n`);
      mail.href = `mailto:${this.dataset.helpEmail}?subject=${subject}&body=${body}`;
      dialog.showModal();
      track('atelier_doubt_open', { piece: piece.id });
      return;
    }
    const reject = event.target.closest('[data-reject]');
    if (reject) {
      const card = reject.closest('[data-piece]');
      const reasons = card.querySelector('[data-reasons]');
      const open = reasons.hidden;
      reasons.hidden = !open;
      reject.setAttribute('aria-expanded', String(open));
      if (open && !this.rejected[card.dataset.piece]) {
        this.rejected[card.dataset.piece] = 'sin motivo';
        card.classList.add('is-dismissed');
        track('atelier_piece_rejected', { piece: card.dataset.piece });
      }
      return;
    }
    const reason = event.target.closest('[data-reason]');
    if (reason) {
      const card = reason.closest('[data-piece]');
      this.rejected[card.dataset.piece] = reason.dataset.reason;
      card.querySelectorAll('[data-reason]').forEach((b) => b.setAttribute('aria-pressed', String(b === reason)));
      track('atelier_piece_rejected', { piece: card.dataset.piece, reason: reason.dataset.reason });
      const list = card.querySelector('[data-reasons]');
      if (!list.querySelector('.sa-reasons__thanks')) {
        list.insertAdjacentHTML(
          'beforeend',
          '<p class="sa-reasons__thanks">Gracias, lo tendremos en cuenta. <button type="button" class="sa-link" data-open-search-inline>Buscar otra opción →</button></p>'
        );
      }
      return;
    }
    const more = event.target.closest('[data-explore-more]');
    if (more) {
      const group = more.closest('.sa-group');
      group.querySelectorAll('[data-explore-extra]').forEach((li) => (li.hidden = false));
      more.remove();
      group.querySelector('[data-explore-extra]')?.querySelector('a, button')?.focus();
      return;
    }
    if (event.target.closest('[data-open-search-inline]')) {
      this.querySelector('[data-open-search]').click();
    }
  }

  /* ---------- Leads ---------- */
  resetForm(form) {
    form.hidden = false;
    form.querySelectorAll('[data-status]').forEach((s) => (s.textContent = ''));
    form.querySelectorAll('[data-error]').forEach((e) => (e.hidden = true));
    form.querySelector('button[type="submit"]').disabled = false;
  }

  onLeadSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const kind = form.dataset.leadForm;
    const emailInput = form.querySelector('[data-email]');
    const error = form.querySelector('[data-error]');
    const email = emailInput.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      error.textContent = 'Escribe un email válido para poder enviarte la selección.';
      error.hidden = false;
      emailInput.setAttribute('aria-invalid', 'true');
      emailInput.focus();
      return;
    }
    error.hidden = true;
    emailInput.removeAttribute('aria-invalid');
    const name = form.querySelector('[data-name]')?.value.trim() || '';
    const consent = !!form.querySelector('[data-consent]')?.checked;
    const message = form.querySelector('[name="message"]')?.value.trim() || '';
    const missing = [...form.querySelectorAll('[name="missing"]:checked')].map((i) => i.value);
    this.contact = { email, name };

    form.querySelector('button[type="submit"]').disabled = true;
    form.querySelector('[data-status]').textContent = 'Enviando…';
    const piece = kind === 'request' ? this.requested : null;
    this.sendLead({ form, kind, email, name, consent, message, missing, piece });
  }

  showSent(form, kind, email) {
    const text = {
      save: `Hecho. Te escribiremos a ${email} con tu selección para que la revises con calma.`,
      request: `Recibido. Te escribiremos a ${email} con el precio total exacto y el plazo antes de cualquier pago.`,
      search: `Recibido. Revisamos más configuraciones y te escribimos a ${email}.`,
      precise: `Recibido. Buscamos tu pieza y te escribimos a ${email}.`,
    }[kind];
    form.hidden = true;
    form.parentElement.querySelector('.sa-sent')?.remove();
    const note = document.createElement('div');
    note.className = 'sa-sent';
    note.setAttribute('role', 'status');
    note.innerHTML = `<p class="sa-sent__title"><span aria-hidden="true">★</span> ${esc(text)}</p><p class="sa-sent__link">Este enlace guarda tu selección: <a href="${esc(this.selectionLink())}">${esc(this.selectionLink().replace(/^https?:\/\//, ''))}</a></p>`;
    form.after(note);
    note.setAttribute('tabindex', '-1');
    note.focus({ preventScroll: true });
    note.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
  }

  showFailed(form) {
    this.resetForm(form);
    const error = form.querySelector('[data-error]');
    error.textContent = `No hemos podido enviarlo. Vuelve a intentarlo o escríbenos a ${this.dataset.helpEmail}.`;
    error.hidden = false;
  }

  leadBody({ kind, message, missing, piece, consent }) {
    const { need, length, tone } = this.answers;
    const rejected = Object.entries(this.rejected).map(([id, reason]) => `${this.pieceById(id)?.name || id} (${reason})`);
    const origin = this.from && this.pieces.find((p) => p.handle === this.from);
    return [
      `ATELIER SEDELLE · ${KIND[kind].label}`,
      `Nivel: ${KIND[kind].level}`,
      `Necesidad: ${NEED[need] || '—'}`,
      `Largo: ${LENGTH[length] || '—'}`,
      `Tono: ${TONE[tone] || '—'}`,
      `Selección mostrada: ${this.shown.map((p) => p.name).join(' · ') || 'ninguna coincidencia'}`,
      piece ? `Pieza solicitada: ${piece.name} (${piece.id})` : null,
      rejected.length ? `Descartadas: ${rejected.join(' · ')}` : null,
      missing.length ? `Qué le falta: ${missing.join(', ')}` : null,
      message ? `Mensaje: ${message}` : null,
      origin ? `Viene de la ficha: ${origin.name}` : null,
      `Acepta novedades por email: ${consent ? 'Sí' : 'No'}`,
      `Enlace a la selección: ${this.selectionLink()}`,
    ]
      .filter(Boolean)
      .join('\n');
  }

  /*
   * Leads use Shopify's native forms, submitted natively so Shopify's own spam protection
   * (hCaptcha, when enabled) can add its token. The page comes back with the form marked as
   * posted; restorePending() then shows the selection again with the confirmation.
   * 1. contact form: the request reaches the Sedelle team, always.
   * 2. customer form: newsletter subscription with Atelier tags, only with the consent box.
   */
  sendLead({ form, kind, email, name, consent, message, missing, piece }) {
    const { need, length, tone } = this.answers;
    const tags = ['atelier-lead', 'circulo-sedelle', KIND[kind].tag];
    if (need) tags.push(`need-${need.replace(/_/g, '-')}`);
    if (length) tags.push(`length-${length}`);
    if (tone) tags.push(`tone-${tone.replace(/_/g, '-')}`);
    const fields = {
      'contact[email]': email,
      'contact[name]': name,
      'contact[Atelier]': KIND[kind].label,
      'contact[Nivel]': KIND[kind].level,
      'contact[body]': this.leadBody({ kind, message, missing, piece, consent }),
    };
    if (piece) fields['contact[Pieza]'] = piece.name;
    const pending = { stage: 'contact', kind, email, name, consent, tags, piece: piece?.id || null, answers: this.answers, from: this.from, shown: this.shown.map((p) => p.id) };
    this.savePending(pending);
    this.submitNative(this.querySelector('#SaContactForm'), fields, form);
  }

  savePending(pending) {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    } catch (e) {
      /* private mode: the request still goes out; only the on-page confirmation is lost */
    }
  }

  submitNative(nativeForm, fields, uiForm) {
    nativeForm.querySelectorAll('[data-generated]').forEach((input) => input.remove());
    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.dataset.generated = '';
      nativeForm.appendChild(input);
    }
    const send = () => (nativeForm.requestSubmit ? nativeForm.requestSubmit() : nativeForm.submit());
    const captcha = window.Shopify?.captcha;
    if (captcha?.protect && !nativeForm.dataset.hcaptchaBound) {
      try {
        captcha.protect(nativeForm, send);
      } catch (e) {
        send();
      }
    } else {
      send();
    }
    // If nothing navigates (check closed, no connection), say so instead of waiting forever,
    // but never while Shopify's human check is on screen.
    clearTimeout(this.nativeTimer);
    const started = Date.now();
    const watch = () => {
      const checking = [...document.querySelectorAll('iframe[src*="hcaptcha"]')].some((frame) => frame.getBoundingClientRect().height > 100);
      const elapsed = Date.now() - started;
      if ((checking && elapsed < 300000) || (!checking && elapsed < 20000)) {
        this.nativeTimer = setTimeout(watch, 2500);
        return;
      }
      if (uiForm) this.showFailed(uiForm);
    };
    this.nativeTimer = setTimeout(watch, 2500);
  }

  restorePending() {
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
      sessionStorage.removeItem(PENDING_KEY);
    } catch (e) {
      return;
    }
    const posted = [...this.querySelectorAll('[data-native-posted]')].map((el) => el.dataset.nativePosted);
    const failed = [...this.querySelectorAll('[data-native-error]')].map((el) => el.dataset.nativeError);
    if (!pending) return;
    if (!posted.includes(pending.stage) && !failed.includes(pending.stage)) return;

    this.answers = pending.answers;
    this.from = pending.from;
    this.contact = { email: pending.email, name: pending.name };
    this.step = 4;
    history.replaceState({ atelier: 4 }, '', this.urlFor(4));
    this.render({ focus: false, animate: false });

    const form = this.leadFormFor(pending);
    if (!form) return;
    if (failed.includes(pending.stage) && pending.stage === 'contact') {
      this.showFailed(form);
      form.scrollIntoView({ block: 'center' });
      return;
    }
    if (pending.stage === 'contact' && pending.consent) {
      // The request is in; now the separate email-list subscription.
      this.savePending({ ...pending, stage: 'customer' });
      form.querySelector('[data-status]').textContent = 'Guardando tus preferencias…';
      const fields = { 'contact[email]': pending.email, 'contact[tags]': pending.tags.join(',') };
      if (pending.name) fields['contact[first_name]'] = pending.name;
      this.submitNative(this.querySelector('#SaCustomerForm'), fields, null);
      return;
    }

    const answers = { kind: pending.kind, need: this.answers.need, length: this.answers.length, tone: this.answers.tone };
    track('atelier_email_submit', answers);
    if (pending.kind === 'request') track('atelier_piece_request', { ...answers, piece: pending.piece || undefined });
    if (pending.kind === 'search' || pending.kind === 'precise') track('atelier_search_request', answers);
    if (pending.consent && posted.includes('customer')) track('atelier_marketing_optin', { kind: pending.kind });
    this.showSent(form, pending.kind, pending.email);
  }

  leadFormFor(pending) {
    if (pending.kind === 'request') {
      const dialog = this.querySelector('[data-request-dialog]');
      const piece = pending.piece && this.pieceById(pending.piece);
      this.requested = piece || null;
      dialog.querySelector('[data-request-name]').textContent = piece ? piece.name : '';
      if (!dialog.open) dialog.showModal();
      return dialog.querySelector('form');
    }
    if (pending.kind === 'search') {
      const panel = this.querySelector('#atelier-busqueda');
      panel.hidden = false;
      this.querySelector('[data-open-search]').setAttribute('aria-expanded', 'true');
      return panel.querySelector('form');
    }
    return this.querySelector(`[data-lead-form="${pending.kind}"]`);
  }
}

if (!customElements.get('sedelle-atelier')) customElements.define('sedelle-atelier', SedelleAtelier);
